import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Link,
  Paper,
  Grid,
  Chip,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  LinkedIn as LinkedInIcon,
  GitHub as GitHubIcon,
  Instagram as InstagramIcon,
  X as XIcon,
  Launch as LaunchIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  Language as WebIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import PageTransition from "../components/PageTransition";
import ScrollReveal from "../components/ScrollReveal";
import StudioPulse from "../components/StudioPulse";
import { DiagramOrgChart } from "../components/StudioDiagrams";
import { HOME_BEATS, ACCENT, SERIF, MEASURE } from "../data/studioStory";
import { captureContactClick } from "../analytics";

// A few flagship products that are live today. Descriptions are grounded in the
// corrected project corpus (chatddk-backend/knowledge/kooi_current_projects.txt).
// This is an explicit selection, not the full portfolio – the section links out
// to Skylark Creations for everything.
const APPS = [
  {
    name: "Decomposer",
    platforms: ["Web"],
    blurb:
      "Turn a sprawling goal into 8–14 concrete task cards – each with honest effort routing (You / AI / Expert) and a built-in quality score.",
    href: "https://decomposer.io/",
  },
  {
    name: "Agentic News",
    platforms: ["Web"],
    blurb:
      "News intelligence that clusters duplicate coverage into single stories and interprets what changed through role-aware lenses, with citations.",
    href: "https://agentic-news.ai",
  },
  {
    name: "BuddhaUR",
    platforms: ["iOS", "Android"],
    blurb:
      "A warm, Socratic guide to early Buddhist wisdom – every substantial claim carries a tappable citation to the original suttas.",
    href: "https://buddha-ur.com/",
  },
  {
    name: "BloomEDU",
    platforms: ["iOS", "Android"],
    blurb:
      "46,000+ wildflowers and spaced repetition that turn fleeting plant sightings into lasting ID skills. Works fully offline.",
    href: "https://bloomedu.app/",
  },
  {
    name: "Reframed",
    platforms: ["Web"],
    blurb:
      "A daily briefing that leads with cited consensus facts, then shows each story through contrasting Left and Right framings.",
    href: "https://reframed.news/",
  },
  {
    name: "Urban Journeys",
    platforms: ["Web"],
    blurb:
      "Themed walking routes with curated stops and accurate distances – optimized for discovery, not speed.",
    href: "https://urban-journeys.com/",
  },
];

const PLATFORM_ICON = {
  Web: WebIcon,
  iOS: AppleIcon,
  Android: AndroidIcon,
};

function PlatformChips({ platforms }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ mb: 1.25 }}>
      {platforms.map((p) => {
        const Icon = PLATFORM_ICON[p] || WebIcon;
        return (
          <Chip
            key={p}
            icon={<Icon sx={{ fontSize: "0.9rem" }} />}
            label={p}
            size="small"
            sx={{ height: 22, fontSize: "0.7rem", "& .MuiChip-icon": { ml: 0.5 } }}
          />
        );
      })}
    </Stack>
  );
}

function SectionEyebrow({ children }) {
  return (
    <Typography
      component="div"
      sx={{
        fontSize: "0.72rem",
        textTransform: "uppercase",
        letterSpacing: "0.2em",
        fontWeight: 600,
        color: "text.secondary",
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/david-d-kooi/", Icon: LinkedInIcon, hover: "#0A66C2" },
  { label: "GitHub", href: "https://github.com/u00dxk2", Icon: GitHubIcon, hover: null },
  { label: "Instagram", href: "https://www.instagram.com/davidkooi", Icon: InstagramIcon, hover: "#E4405F" },
  { label: "X", href: "https://x.com/davidkooi", Icon: XIcon, hover: "#1DA1F2" },
];

function HomePage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <PageTransition>
      <Helmet>
        <title>David Kooi – A one-person, AI-native product studio</title>
        <link rel="canonical" href="https://davidkooi.com/" />
        <link
          rel="alternate"
          type="application/json"
          href="https://davidkooi.com/pulse.json"
          title="What the studio did — daily shipped-work snapshot"
        />
        <meta
          name="description"
          content="David Kooi runs Skylark Creations, a one-person, AI-native product studio in Colorado – a portfolio of products, several already live across iOS, Android, and web, built with a fleet of AI agents and measured by one thing: would you tell a friend?"
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="David Kooi – One-person, AI-native product studio" />
        <meta
          property="og:description"
          content="One founder, a fleet of AI agents, products built to be loved. See how the studio runs – and what it ships."
        />
        <meta property="og:url" content="https://davidkooi.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="David Kooi – AI-native product studio" />
        <meta
          name="twitter:description"
          content="One founder, a fleet of AI agents, products built to be loved."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Person",
                "@id": "https://davidkooi.com/#david",
                name: "David Kooi",
                url: "https://davidkooi.com",
                email: "hello@skylarkcreations.com",
                sameAs: [
                  "https://www.linkedin.com/in/david-d-kooi/",
                  "https://github.com/u00dxk2",
                  "https://www.instagram.com/davidkooi",
                  "https://x.com/davidkooi",
                  "https://www.threads.com/@davidkooi",
                  "https://uncagedminds.substack.com/",
                ],
                jobTitle: "Founder, Skylark Creations",
                worksFor: { "@id": "https://skylarkcreations.com/#studio" },
                address: {
                  "@type": "PostalAddress",
                  addressRegion: "CO",
                  addressCountry: "US",
                },
                knowsAbout: [
                  "AI agent orchestration",
                  "Agentic software development",
                  "Product strategy",
                  "Retrieval-augmented generation",
                  "iOS, Android, and web application development",
                  "Verification and audit of AI-generated code",
                ],
                alumniOf: [
                  "Northwestern University",
                  "University of Chicago Booth School of Business",
                ],
                description:
                  "David Kooi runs Skylark Creations, a one-person, AI-native product studio based in Colorado – a portfolio of products, several already live across iOS, Android, and web, built with a fleet of AI agents.",
              },
              {
                "@type": "ProfessionalService",
                "@id": "https://skylarkcreations.com/#studio",
                name: "Skylark Creations",
                url: "https://skylarkcreations.com",
                email: "hello@skylarkcreations.com",
                founder: { "@id": "https://davidkooi.com/#david" },
                areaServed: "Worldwide (remote, async-first)",
                address: {
                  "@type": "PostalAddress",
                  addressRegion: "CO",
                  addressCountry: "US",
                },
                contactPoint: {
                  "@type": "ContactPoint",
                  contactType: "new engagements",
                  email: "hello@skylarkcreations.com",
                  availableLanguage: "English",
                },
                description:
                  "A one-person, AI-native product studio. Independent AI-orchestration consulting: one human orchestrating planning, verification, and delivery across a fleet of AI agents, inside a client's codebase and working practice.",
                makesOffer: [
                  {
                    "@type": "Offer",
                    availability: "https://schema.org/InStock",
                    itemOffered: {
                      "@type": "Service",
                      name: "AI-orchestration consulting",
                      serviceType: "AI-orchestration consulting",
                      description:
                        "Running a product engagement end to end – spec, build, verification, and deployed code – with one human orchestrating a fleet of AI agents. Async-first, no standing meetings, billed on engaged attention rather than machine time.",
                    },
                  },
                  {
                    "@type": "Offer",
                    availability: "https://schema.org/InStock",
                    itemOffered: {
                      "@type": "Service",
                      name: "Verification and audit of AI-generated code",
                      serviceType: "Software audit",
                      description:
                        "Adversarial cross-model review as a release gate: a second model family is prompted to refute the work, and whatever survives is re-verified against live code.",
                    },
                  },
                  {
                    "@type": "Offer",
                    availability: "https://schema.org/InStock",
                    itemOffered: {
                      "@type": "Service",
                      name: "Agent-practice review and coaching",
                      serviceType: "Technical advisory",
                      description:
                        "Reviewing and coaching a team already building with AI agents – the role an engagement inverts into when the client has already built the thing.",
                    },
                  },
                ],
              },
            ],
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
        <Navigation />

        {/* Landmark for the header skip link; content is not re-indented
            so the diff stays two lines. */}
        <Box component="main" id="main">

        {/* ===================== Hero ===================== */}
        <Box
          sx={{
            background: `radial-gradient(1100px 480px at 50% -8%, ${alpha(
              ACCENT,
              isDark ? 0.12 : 0.09
            )}, transparent 62%), ${theme.palette.background.default}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Container maxWidth="md" sx={{ pt: { xs: 7, md: 11 }, pb: { xs: 6, md: 9 } }}>
            <ScrollReveal>
              <Box sx={{ textAlign: { xs: "left", md: "center" }, maxWidth: 760, mx: "auto" }}>
                <SectionEyebrow>David Kooi · Skylark Creations</SectionEyebrow>
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: SERIF,
                    fontWeight: 600,
                    fontSize: { xs: "2.4rem", sm: "3.1rem", md: "3.6rem" },
                    lineHeight: 1.08,
                    letterSpacing: "-0.015em",
                    color: "text.primary",
                  }}
                >
                  One person.
                  <br />
                  A fleet of AI agents.
                  <br />
                  Products built to be loved.
                </Typography>
                <Typography
                  sx={{
                    mt: 3,
                    fontSize: { xs: "1.05rem", md: "1.2rem" },
                    lineHeight: 1.65,
                    color: "text.secondary",
                    maxWidth: 640,
                    mx: { xs: 0, md: "auto" },
                  }}
                >
                  I'm David Kooi. I run a one-person, AI-native product studio in
                  Colorado – I design, build, and ship software, and I measure
                  success by one thing: whether someone would tell a friend about it.
                  In 2026 the same studio system started running client engagements
                  end to end.
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mt: 4, justifyContent: { xs: "flex-start", md: "center" } }}
                >
                  <Button
                    component={RouterLink}
                    to="/studio"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 3.5,
                      py: 1.4,
                      fontSize: "1rem",
                      backgroundColor: ACCENT,
                      color: "accent.contrastText",
                      boxShadow: `0 8px 24px ${alpha(ACCENT, 0.35)}`,
                      "&:hover": {
                        backgroundColor: "hsl(26 95% 44%)",
                        transform: "translateY(-2px)",
                        boxShadow: `0 12px 30px ${alpha(ACCENT, 0.45)}`,
                      },
                    }}
                  >
                    See how the studio runs
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/chat"
                    variant="outlined"
                    sx={{
                      px: 3.5,
                      py: 1.4,
                      fontSize: "1rem",
                      color: "text.primary",
                      borderColor: alpha(theme.palette.text.primary, 0.25),
                      "&:hover": {
                        borderColor: ACCENT,
                        backgroundColor: alpha(ACCENT, 0.08),
                      },
                    }}
                  >
                    Chat with ChatDDK
                  </Button>
                </Stack>
              </Box>
            </ScrollReveal>

            {/* The proof, directly under the claim. Everything below this point
                on the page is evidence for something the visitor has already
                watched happen. */}
            <ScrollReveal delay={0.1}>
              <Box sx={{ maxWidth: 820, mx: "auto" }}>
                <StudioPulse links={APPS} />
              </Box>
            </ScrollReveal>
          </Container>
        </Box>

        {/* ===================== How the studio works ===================== */}
        <Container maxWidth="md" sx={{ py: { xs: 7, md: 9 } }}>
          <ScrollReveal>
            <Box sx={{ textAlign: "center", maxWidth: MEASURE, mx: "auto" }}>
              <SectionEyebrow>How the studio works</SectionEyebrow>
              <Typography
                component="h2"
                sx={{
                  fontFamily: SERIF,
                  fontWeight: 600,
                  fontSize: { xs: "1.8rem", md: "2.3rem" },
                  color: "text.primary",
                }}
              >
                How one person keeps up with a portfolio of products
              </Typography>
              <Typography sx={{ mt: 2, color: "text.secondary", lineHeight: 1.7 }}>
                Four manager AIs run their own fleets – the product portfolio and
                three client engagements – over a shared channel everyone can see.
                Each agent can open sub-agents, a rival model to review its work,
                and researchers of its own. I step in only for taste, strategy, and
                the calls a human must make.
              </Typography>
            </Box>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <Box sx={{ mt: 4 }}>
              <DiagramOrgChart />
            </Box>
          </ScrollReveal>

          <ScrollReveal delay={0.15}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {HOME_BEATS.map((beat) => (
                <Grid item xs={12} sm={6} key={beat.n}>
                  <Box
                    sx={{
                      height: "100%",
                      p: 2.5,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: isDark
                        ? alpha(theme.palette.common.white, 0.02)
                        : alpha(theme.palette.common.black, 0.015),
                    }}
                  >
                    {/* No numbers here. These four are an excerpt of the ten on
                        /studio, so they read "01, 02, 03, 08" — which looks like
                        a mistake to anyone who hasn't seen the full list. */}
                    <Typography
                      sx={{ fontWeight: 700, fontSize: "1.02rem", color: "text.primary" }}
                    >
                      {beat.title}
                    </Typography>
                    <Typography sx={{ mt: 1, color: "text.secondary", lineHeight: 1.6 }}>
                      {beat.line}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                component={RouterLink}
                to="/studio"
                variant="text"
                endIcon={<ArrowForwardIcon />}
                sx={{ color: "accent.text", fontWeight: 600 }}
              >
                See all ten parts
              </Button>
            </Box>
          </ScrollReveal>
        </Container>

        {/* ===================== See it running ===================== */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          {/* Wider than the rest of the page on purpose. These are 1380px and
              881px dashboards; two-up inside the md container they rendered at
              ~400px, which is where "proof" turns into texture. The lg
              container lets each sit at something near its own resolution. */}
          <Container maxWidth="lg" sx={{ py: { xs: 7, md: 9 } }}>
            <ScrollReveal>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "0.85fr 1.15fr" },
                  gap: { xs: 2, md: 7 },
                  alignItems: "start",
                }}
              >
                <Box>
                  <SectionEyebrow>See it running</SectionEyebrow>
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "1.8rem", md: "2.4rem" },
                      lineHeight: 1.15,
                      color: "text.primary",
                    }}
                  >
                    It's not theoretical – it runs every day
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.75,
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    maxWidth: MEASURE,
                    pt: { md: 1 },
                  }}
                >
                  The whole studio runs on a private command center, where every
                  product's daily status, the agents' live activity, and the
                  current priorities sit in one place.
                </Typography>
              </Box>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <Stack spacing={{ xs: 4, md: 6 }} sx={{ mt: { xs: 4, md: 6 } }}>
                {[
                  {
                    src: "/images/command-center-1.png",
                    // Native pixel width. Never render wider than this: upscaling
                    // a dashboard is how a legible screenshot becomes a blurry one.
                    naturalWidth: 1380,
                    alt: "The studio command center showing every product and what shipped.",
                    caption: "Every product, what shipped, and whether anything needs me.",
                  },
                  {
                    src: "/images/command-center-2.png",
                    naturalWidth: 881,
                    alt: "The command center's daily reports across the portfolio.",
                    caption: "Each product files a daily report – a recent day across the portfolio.",
                  },
                ].map((shot) => (
                  <Box component="figure" key={shot.src} sx={{ m: 0 }}>
                    <Box
                      component="a"
                      href={shot.src}
                      target="_blank"
                      rel="noopener"
                      aria-label={`Open full size: ${shot.alt}`}
                      sx={{
                        display: "block",
                        maxWidth: shot.naturalWidth,
                        mx: "auto",
                        borderRadius: 2,
                        overflow: "hidden",
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "border-color 0.2s ease",
                        "&:hover": { borderColor: ACCENT },
                      }}
                    >
                      <Box
                        component="img"
                        src={shot.src}
                        alt={shot.alt}
                        loading="lazy"
                        width={shot.naturalWidth}
                        sx={{ width: "100%", height: "auto", display: "block" }}
                      />
                    </Box>
                    <Typography
                      component="figcaption"
                      sx={{
                        mt: 1.25,
                        fontSize: "0.85rem",
                        color: "text.secondary",
                        maxWidth: shot.naturalWidth,
                        mx: "auto",
                      }}
                    >
                      {shot.caption}{" "}
                      <Box component="span" sx={{ color: "accent.text", fontWeight: 600 }}>
                        Open full size ↗
                      </Box>
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </ScrollReveal>
          </Container>
        </Box>

        {/* ===================== A few that are live ===================== */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <Container maxWidth="md" sx={{ py: { xs: 7, md: 9 } }}>
            <ScrollReveal>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                  gap: { xs: 2, md: 5 },
                  alignItems: "end",
                  mb: 4,
                }}
              >
                <Box>
                  <SectionEyebrow>A few that are live</SectionEyebrow>
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "1.8rem", md: "2.4rem" },
                      lineHeight: 1.15,
                      color: "text.primary",
                    }}
                  >
                    Products you can use today
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.7,
                    maxWidth: "34ch",
                    pb: { md: 0.5 },
                  }}
                >
                  A selection of what's shipped across web, iOS, and Android. The
                  full portfolio lives at Skylark Creations.
                </Typography>
              </Box>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <Grid container spacing={2.5}>
                {APPS.map((app) => (
                  <Grid item xs={12} sm={6} md={4} key={app.name}>
                    <Paper
                      elevation={0}
                      // The whole card is the link. It already lifted on hover,
                      // so it looked clickable while only the 20px "Try it" was.
                      component="a"
                      href={app.href}
                      target="_blank"
                      rel="noopener"
                      sx={{
                        p: 2.5,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2,
                        textDecoration: "none",
                        color: "inherit",
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: isDark
                          ? alpha(theme.palette.common.white, 0.02)
                          : theme.palette.common.white,
                        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          borderColor: alpha(ACCENT, 0.5),
                          boxShadow: isDark
                            ? "0 14px 40px rgba(0,0,0,0.45)"
                            : "0 14px 40px rgba(0,0,0,0.10)",
                        },
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", mb: 1 }}>
                        {app.name}
                      </Typography>
                      <PlatformChips platforms={app.platforms} />
                      <Typography
                        sx={{ color: "text.secondary", lineHeight: 1.55, fontSize: "0.9rem", mb: 2, flexGrow: 1 }}
                      >
                        {app.blurb}
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          color: "accent.text",
                        }}
                      >
                        Try it <LaunchIcon sx={{ fontSize: "0.9rem" }} />
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Button
                  href="https://skylarkcreations.com"
                  target="_blank"
                  rel="noopener"
                  variant="text"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ color: "accent.text", fontWeight: 600 }}
                >
                  See the whole portfolio at Skylark Creations
                </Button>
              </Box>
            </ScrollReveal>
          </Container>
        </Box>

        {/* ===================== ChatDDK ===================== */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <Container maxWidth="md" sx={{ py: { xs: 7, md: 9 } }}>
            <ScrollReveal>
              {/* Was a centred block of copy inside a filled panel — the fourth
                  centred stack in a row, and the one element on the page that
                  read as a stock CTA. Same content, laid out; the accent now
                  edges the panel instead of filling it. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
                  gap: { xs: 3, md: 6 },
                  alignItems: "center",
                  borderRadius: 3,
                  border: `1px solid ${alpha(ACCENT, 0.35)}`,
                  borderLeft: `3px solid ${ACCENT}`,
                  backgroundColor: alpha(ACCENT, isDark ? 0.06 : 0.045),
                  p: { xs: 3, md: 4.5 },
                }}
              >
                <Box>
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: { xs: "1.6rem", md: "2.1rem" },
                      lineHeight: 1.15,
                      color: "text.primary",
                    }}
                  >
                    Talk to a digital version of me
                  </Typography>
                  <Typography
                    sx={{ mt: 1.5, color: "text.secondary", lineHeight: 1.7, maxWidth: MEASURE }}
                  >
                    ChatDDK is an AI persona trained on my writing, history, and
                    frameworks – it thinks and writes like me on a good day. Ask it
                    about AI, product, philosophy, or what I'm building.
                  </Typography>
                </Box>
                <Button
                  component={RouterLink}
                  to="/chat"
                  variant="contained"
                  sx={{
                    px: 3.5,
                    py: 1.4,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    fontSize: "1rem",
                    backgroundColor: ACCENT,
                    color: "accent.contrastText",
                    boxShadow: `0 8px 24px ${alpha(ACCENT, 0.35)}`,
                    "&:hover": {
                      backgroundColor: "hsl(26 95% 44%)",
                      transform: "translateY(-2px)",
                      boxShadow: `0 12px 30px ${alpha(ACCENT, 0.45)}`,
                    },
                  }}
                >
                  Chat with ChatDDK
                </Button>
              </Box>
            </ScrollReveal>
          </Container>
        </Box>

        {/* ===================== Before Skylark + connect ===================== */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <Container maxWidth="md" sx={{ py: { xs: 7, md: 9 } }}>
            <ScrollReveal>
              <Box sx={{ maxWidth: MEASURE, mx: "auto" }}>
                <SectionEyebrow>Before Skylark</SectionEyebrow>
                <Typography sx={{ color: "text.primary", lineHeight: 1.8, fontSize: "1.05rem" }}>
                  {/* "Jointly" was linked to the Uncaged Minds Substack — the same
                      destination the newsletter link two sentences down already
                      names. A company name pointing at an unrelated newsletter
                      reads as a mistake, and Jointly has no live site to point at,
                      so it stays plain text here exactly as it does on /about. */}
                  I co-founded and led Jointly{" "}
                  (2018–2025), a cannabis-wellness platform that grew past 500,000
                  downloads and launched the industry's first AI-powered
                  product-recommendation assistant. Earlier I built and sold Santa
                  Monica Mountains Cyclery to Trek Bicycle after ten straight years
                  of growth, and ran a $1B+ distribution operation at McMaster-Carr.
                  I hold an MBA from Chicago Booth, a B.A. from Northwestern, and a
                  B.S. in Computer Science from Oregon State. I write about AI,
                  philosophy, and product on{" "}
                  <Link href="https://uncagedminds.substack.com" target="_blank" rel="noopener" underline="hover" sx={{ color: "accent.text" }}>
                    Uncaged Minds
                  </Link>
                  .
                </Typography>
                {/* A plain, scannable address. Three cold-evaluator persona runs
                    all ended without finding any way to contact him — one guessed
                    a contact page "probably" existed, one assumed the Chat link
                    was it. Nobody who won't fill in a form should have to navigate
                    to a second page to find an email. */}
                <Typography sx={{ mt: 3, color: "text.primary" }}>
                  Working on something and want a hand?{" "}
                  <Link
                    href="mailto:hello@skylarkcreations.com"
                    onClick={() => captureContactClick("home-cta")}
                    underline="hover"
                    sx={{ color: "accent.text", fontWeight: 600 }}
                  >
                    hello@skylarkcreations.com
                  </Link>
                  {" — or see "}
                  <Link
                    component={RouterLink}
                    to="/hire"
                    underline="hover"
                    sx={{ color: "accent.text", fontWeight: 600 }}
                  >
                    how engagements run
                  </Link>
                  {" first."}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Button
                    component={RouterLink}
                    to="/about"
                    variant="text"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ color: "accent.text", fontWeight: 600, px: 0 }}
                  >
                    More about me
                  </Button>
                </Box>

                <Stack
                  direction="row"
                  spacing={2}
                  flexWrap="wrap"
                  sx={{ gap: 1.5, mt: 4 }}
                >
                  {SOCIALS.map(({ label, href, Icon, hover }) => (
                    <Tooltip title={label} arrow key={label}>
                      <Button
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<Icon />}
                        variant="outlined"
                        sx={{
                          color: "text.primary",
                          borderColor: alpha(theme.palette.text.primary, 0.25),
                          "&:hover": {
                            borderColor: hover || ACCENT,
                            backgroundColor: alpha(hover || ACCENT, 0.1),
                            color: hover || (isDark ? "#fff" : "text.primary"),
                          },
                        }}
                      >
                        {label}
                      </Button>
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            </ScrollReveal>
          </Container>
        </Box>

        </Box>

        <Footer />
      </Box>
    </PageTransition>
  );
}

export default HomePage;
